import axios from 'axios';
import { TOKEN } from '@nxsys/store/constants/keys';
import { resolveAuthTarget } from '@nxsys/utils/authTarget';
import { handleSessionExpired } from '@nxsys/services/session/sessionExpiry';
import {
  PLATFORM_HEADER,
  PLATFORM_WEB,
  SESSION_EXPIRED_MESSAGE,
} from '@nxsys/constants/session';

const apiConfig = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

apiConfig.interceptors.request.use((config) => {
  const { authCode, isBureau } = resolveAuthTarget();

  config.baseURL = isBureau
    ? import.meta.env.VITE_API_URL_BUREAU
    : import.meta.env.VITE_API_URL;

  if (isBureau) {
    config.headers['Accept'] = 'application/vnd.user-backend.v1';
  }

  // Marks the request as coming from the New UI. The backend stamps this onto
  // the tokens it mints and only enforces the idle expiry on tokens carrying
  // it, so mobile and the classic UI keep their non-expiring tokens (NX-5238).
  config.headers[PLATFORM_HEADER] = PLATFORM_WEB;

  if (authCode) {
    config.params = {
      auth_code: authCode,
      ...config.params,
    };
  } else {
    const token = localStorage.getItem(TOKEN);
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

// Response interceptor: normalize errors and prevent undefined data crashes
apiConfig.interceptors.response.use(
  (response) => response,
  (error) => {
    // Log for debugging (visible in browser console)
    if (import.meta.env.DEV) {
      console.error(
        `[API Error] ${error?.config?.method?.toUpperCase()} ${error?.config?.url}`,
        error?.response?.status,
        error?.response?.data?.message || error?.message,
      );
    }
    // An expired session must land the user on the login screen rather than
    // surfacing as an error toast on whatever they were doing (NX-5238).
    //
    // Matched on the reason, not on the status: a 401 is also how the backend
    // reports a blocked account, an agency with portal access withdrawn, and a
    // login-as session the real user has taken back. Signing those out with
    // "you were inactive" would be a lie, and the teardown clears localStorage,
    // which would take every other tab with it. Only the message JwtStrategy
    // sends for an expired token counts. It also makes the endpoint the request
    // went to irrelevant, so /auth/login answering 401 to a wrong password stays
    // an ordinary error while /auth/login-as-* on a dead session redirects.
    if (
      error?.response?.status === 401 &&
      error?.response?.data?.message === SESSION_EXPIRED_MESSAGE
    ) {
      handleSessionExpired();
    }

    return Promise.reject(error);
  },
);

export default apiConfig;
